import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  MessageSquare, Send, X, AlertCircle, CheckCircle, 
  Clock, ArrowLeft, Filter, Inbox, MessageCircle
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { apiErrorMessage, isApiSuccess, parseApiBody } from '../../utils/apiResponse';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

/**
 * Tab Feedback per Gym Dashboard
 * Permette alle palestre di:
 * - Inviare feedback agli admin
 * - Visualizzare storico feedback
 * - Rispondere in conversazioni esistenti
 */
export default function FeedbackTab() {
  const { getAuthenticatedApi } = useAuth();
  
  // State principale
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const closeFeedbackTimerRef = useRef(null);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State form nuovo feedback
  const [newFeedback, setNewFeedback] = useState({
    type: 'suggestion',
    title: '',
    description: '',
    email: '',
    severity: 'medium'
  });
  
  // State risposta
  const [replyMessage, setReplyMessage] = useState('');
  
  // State filtri
  const [filterStatus, setFilterStatus] = useState('all');
  const [newFeedbackOpen, setNewFeedbackOpen] = useState(false);

  useEffect(() => {
    loadFeedbacks();
    return () => {
      if (closeFeedbackTimerRef.current) {
        clearTimeout(closeFeedbackTimerRef.current);
      }
    };
  }, []);

  // Carica feedback della gym
  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      const response = await api.get('/feedback_api.php');
      
      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
      }
    } catch (err) {
      console.error('Errore caricamento feedback:', err);
      setError('Errore nel caricamento dei feedback');
    } finally {
      setLoading(false);
    }
  };

  // Carica conversazione di un feedback
  const loadConversation = async (feedbackId) => {
    try {
      setLoadingResponses(true);
      const api = getAuthenticatedApi();
      const response = await api.get(`/feedback_responses_api.php?feedback_id=${feedbackId}`);
      
      if (response.data.success) {
        setResponses(response.data.responses || []);
        setError(''); // Clear any previous errors
      } else {
        console.error('API Error:', response.data.error);
        setError('Errore nel caricamento della conversazione: ' + (response.data.error || 'Errore sconosciuto'));
      }
    } catch (err) {
      console.error('Errore caricamento conversazione:', err);
      setError('Errore nel caricamento della conversazione');
    } finally {
      setLoadingResponses(false);
    }
  };

  const emptyFeedbackForm = {
    type: 'suggestion',
    title: '',
    description: '',
    email: '',
    severity: 'medium'
  };

  const confirmFeedbackSent = () => {
    setNewFeedback(emptyFeedbackForm);
    setFormError('');
    setFeedbackSent(true);
    setSuccess('Feedback inviato con successo!');
    setTimeout(() => setSuccess(''), 4000);
    loadFeedbacks();
    if (closeFeedbackTimerRef.current) {
      clearTimeout(closeFeedbackTimerRef.current);
    }
    closeFeedbackTimerRef.current = setTimeout(() => {
      setNewFeedbackOpen(false);
      setFeedbackSent(false);
    }, 1400);
  };

  // Invia nuovo feedback
  const handleSubmitNewFeedback = async (e) => {
    e.preventDefault();

    if (sendingFeedback) {
      return;
    }
    
    // Validazione
    if (!newFeedback.title.trim() || !newFeedback.description.trim() || !newFeedback.email.trim()) {
      setFormError('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      setSendingFeedback(true);
      setFormError('');
      setError('');
      
      const api = getAuthenticatedApi();
      const response = await api.post('/feedback_api.php', newFeedback);
      const body = parseApiBody(response?.data);

      if (isApiSuccess(body)) {
        confirmFeedbackSent();
        return;
      }

      setFormError(apiErrorMessage(body, 'Errore nell\'invio del feedback'));
    } catch (err) {
      console.error('Errore invio feedback:', err);
      const body = parseApiBody(err?.response?.data);
      if (isApiSuccess(body)) {
        confirmFeedbackSent();
        return;
      }
      setFormError(apiErrorMessage(body, 'Errore nell\'invio del feedback'));
    } finally {
      setSendingFeedback(false);
    }
  };

  // Invia risposta a feedback esistente
  const handleSendReply = async () => {
    const message = replyMessage.trim();
    if (!message) {
      setError('Scrivi un messaggio prima di inviare');
      return;
    }
    
    if (sendingReply) {
      return;
    }
    
    try {
      setSendingReply(true);
      setError('');
      
      const api = getAuthenticatedApi();
      const response = await api.post('/feedback_responses_api.php', {
        feedback_id: selectedFeedback.id,
        message
      });
      const body = parseApiBody(response?.data);

      if (!isApiSuccess(body)) {
        setError(apiErrorMessage(body, 'Errore nell\'invio della risposta'));
        return;
      }

      setReplyMessage('');
      setResponses((prev) => [
        ...prev,
        {
          id: body?.response_id || `local-${Date.now()}`,
          message,
          is_admin_response: 0,
          created_at: new Date().toISOString()
        }
      ]);
      setSuccess('Risposta inviata con successo!');
      setTimeout(() => setSuccess(''), 4000);

      await loadConversation(selectedFeedback.id);
      loadFeedbacks();
    } catch (err) {
      console.error('Errore invio risposta:', err);
      const body = parseApiBody(err?.response?.data);
      if (isApiSuccess(body)) {
        setReplyMessage('');
        setSuccess('Risposta inviata con successo!');
        setTimeout(() => setSuccess(''), 4000);
        await loadConversation(selectedFeedback.id);
        loadFeedbacks();
        return;
      }
      setError(apiErrorMessage(body, 'Errore nell\'invio della risposta'));
    } finally {
      setSendingReply(false);
    }
  };

  // Apri conversazione
  const handleOpenFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    loadConversation(feedback.id);
  };

  // Chiudi conversazione
  const handleCloseFeedback = () => {
    setSelectedFeedback(null);
    setResponses([]);
    setReplyMessage('');
  };

  // Filtra feedback per status
  const filteredFeedbacks = feedbacks.filter(f => {
    if (filterStatus === 'all') return true;
    return f.status === filterStatus;
  });

  // Badge status
  const getStatusBadge = (status) => {
    const badges = {
      'new': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', label: 'Nuovo' },
      'in_progress': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', label: 'In Lavorazione' },
      'closed': { color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', label: 'Chiuso' },
      'rejected': { color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', label: 'Rifiutato' }
    };
    return badges[status] || badges['new'];
  };

  // Badge severity
  const getSeverityBadge = (severity) => {
    const badges = {
      'low': { color: 'bg-gray-100 text-gray-800', label: 'Bassa', icon: '🟢' },
      'medium': { color: 'bg-blue-100 text-blue-800', label: 'Media', icon: '🟡' },
      'high': { color: 'bg-orange-100 text-orange-800', label: 'Alta', icon: '🟠' },
      'critical': { color: 'bg-red-100 text-red-800', label: 'Critica', icon: '🔴' }
    };
    return badges[severity] || badges['medium'];
  };

  // Vista conversazione
  if (selectedFeedback) {
    return (
      <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header conversazione */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-white/10 p-6">
          <button
            onClick={handleCloseFeedback}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 dark:text-white/65 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alla lista
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {decodeHtmlEntities((selectedFeedback.title || '').trim()) || 'Feedback senza titolo'}
              </h2>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(selectedFeedback.status).color}`}>
                  {getStatusBadge(selectedFeedback.status).label}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getSeverityBadge(selectedFeedback.severity).color}`}>
                  {getSeverityBadge(selectedFeedback.severity).icon} {getSeverityBadge(selectedFeedback.severity).label}
                </span>
                <span className="text-sm text-gray-500 dark:text-white/65">
                  ID: #{selectedFeedback.id}
                </span>
              </div>
            </div>
          </div>
          
          {/* Messaggio originale */}
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-gray-700 dark:text-white/85">{decodeHtmlEntities(selectedFeedback.description)}</p>
          </div>
        </div>

        {/* Conversazione */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loadingResponses ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-white/65">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna risposta ancora. Attendi che l'admin ti risponda.</p>
            </div>
          ) : (
            responses.map((response) => (
              <div
                key={response.id}
                className={`flex ${response.is_admin_response ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-2xl ${response.is_admin_response ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-green-50 dark:bg-green-900/20'} rounded-lg p-4 shadow-sm`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`font-semibold ${response.is_admin_response ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300'}`}>
                      {response.is_admin_response ? '👨‍💼 Admin' : '🏢 Tu'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-white/65">
                      {new Date(response.created_at).toLocaleString('it-IT')}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-white/85 whitespace-pre-wrap">
                    {decodeHtmlEntities(response.message)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input risposta (solo se ticket non chiuso) */}
        {selectedFeedback.status !== 'closed' && selectedFeedback.status !== 'rejected' && (
          <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-white/10 p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                {success}
              </div>
            )}
            
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Scrivi un messaggio
                </label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white resize-none"
                  placeholder="Scrivi la tua risposta..."
                  disabled={sendingReply}
                />
              </div>
              <button
                onClick={handleSendReply}
                disabled={sendingReply || !replyMessage.trim()}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2 h-fit"
                type="button"
              >
                {sendingReply ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Invia
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        
        {selectedFeedback.status === 'closed' && (
          <div className="bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-white/10 p-6 text-center">
            <p className="text-gray-600 dark:text-white/65">
              ✅ Questo ticket è stato chiuso dall'admin
            </p>
          </div>
        )}
      </div>
    );
  }

  // Vista lista feedback
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <h2 className="text-2xl font-bold mb-2">
          Feedback & Supporto
        </h2>
        <p className="text-indigo-100">
          Invia feedback agli amministratori o visualizza le tue richieste precedenti
        </p>
      </div>

      {/* Messaggi */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      {success && (
        <div className="mx-6 mt-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {/* Lista feedback */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            I Miei Feedback
          </h3>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setError(''); setFormError(''); setFeedbackSent(false); setSuccess(''); setNewFeedbackOpen(true); }}
              className="px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Nuovo feedback
            </button>

            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
              >
                <option value="all">Tutti</option>
                <option value="new">Nuovi</option>
                <option value="in_progress">In Lavorazione</option>
                <option value="closed">Chiusi</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : filteredFeedbacks.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-white/65 mb-2">
              {filterStatus === 'all' ? 'Nessun feedback inviato' : 'Nessun feedback con questo filtro'}
            </p>
            <p className="text-sm text-gray-400 dark:text-white/50">
              {filterStatus === 'all' ? 'Invia il tuo primo feedback usando il form qui sopra' : 'Prova a cambiare il filtro'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFeedbacks.map((feedback) => (
              <div
                key={feedback.id}
                onClick={() => handleOpenFeedback(feedback)}
                className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-white/10 p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {decodeHtmlEntities((feedback.title || '').trim()) || 'Feedback senza titolo'}
                      </h4>
                    </div>
                    
                    <p className="text-sm text-gray-600 dark:text-white/65 line-clamp-2 mb-2">
                      {decodeHtmlEntities(feedback.description)}
                    </p>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(feedback.status).color}`}>
                        {getStatusBadge(feedback.status).label}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityBadge(feedback.severity).color}`}>
                        {getSeverityBadge(feedback.severity).icon} {getSeverityBadge(feedback.severity).label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-white/65">
                        #{feedback.id}
                      </span>
                    </div>
                  </div>
                  
                  <div className="ml-4 text-right">
                    <p className="text-xs text-gray-500 dark:text-white/65 mb-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {new Date(feedback.created_at).toLocaleDateString('it-IT')}
                    </p>
                    {feedback.response_count > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                        <MessageCircle className="h-3 w-3 inline mr-1" />
                        {feedback.response_count} rispost{feedback.response_count === 1 ? 'a' : 'e'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Popup: Nuovo Feedback */}
      <Dialog open={newFeedbackOpen} onOpenChange={(open) => {
        setNewFeedbackOpen(open);
        if (!open) {
          if (closeFeedbackTimerRef.current) {
            clearTimeout(closeFeedbackTimerRef.current);
          }
          setFeedbackSent(false);
          setFormError('');
        }
      }}>
        <DialogContent className="max-w-2xl w-[92vw] max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Nuovo feedback
            </DialogTitle>
            <DialogDescription>
              Compila i campi e invia la richiesta agli amministratori.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto pr-1">
            {feedbackSent ? (
              <div className="py-10 text-center">
                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900 dark:text-white">Feedback inviato</p>
                <p className="text-sm text-gray-600 dark:text-white/70 mt-2">
                  La richiesta è stata registrata. La trovi nell&apos;elenco qui sotto.
                </p>
              </div>
            ) : null}
            {!feedbackSent && formError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                {formError}
              </div>
            )}
            {!feedbackSent && (
            <form onSubmit={handleSubmitNewFeedback} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                    Tipo <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newFeedback.type}
                    onChange={(e) => setNewFeedback({ ...newFeedback, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                    required
                  >
                    <option value="suggestion">💡 Suggerimento</option>
                    <option value="feature">✨ Richiesta Feature</option>
                    <option value="bug">🐛 Bug/Problema</option>
                    <option value="complaint">⚠️ Reclamo</option>
                    <option value="compliment">👍 Complimento</option>
                    <option value="other">📝 Altro</option>
                  </select>
                </div>

                {/* Priorità */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                    Priorità <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newFeedback.severity}
                    onChange={(e) => setNewFeedback({ ...newFeedback, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                    required
                  >
                    <option value="low">🟢 Bassa</option>
                    <option value="medium">🟡 Media</option>
                    <option value="high">🟠 Alta</option>
                    <option value="critical">🔴 Critica</option>
                  </select>
                </div>
              </div>

              {/* Titolo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                  Titolo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newFeedback.title}
                  onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Es. Problema con fatturazione"
                  required
                />
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                  Descrizione <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newFeedback.description}
                  onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white resize-none"
                  placeholder="Descrivi il problema o il suggerimento in dettaglio..."
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                  Email per risposta <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newFeedback.email}
                  onChange={(e) => setNewFeedback({ ...newFeedback, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                  placeholder="tua-email@palestra.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={sendingFeedback}
                className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {sendingFeedback ? <LoadingSpinner size="sm" /> : <Send className="h-4 w-4" />}
                {sendingFeedback ? 'Invio in corso...' : 'Invia feedback'}
              </button>
            </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

